// De qué anuncio salió cada alta.
//
// ─── El problema que resuelve ──────────────────────────────────────────────
//
// El anuncio cae en `holaturnos.com/?utm_source=...`, que es la landing: un
// HTML suelto. De ahí la persona toca "Probar gratis" y llega a `/registro`,
// que es OTRO documento y ya no tiene los parámetros en la URL. Si no se
// guardan en el camino, para cuando hay una cuenta que atribuir no queda
// rastro de qué anuncio la trajo.
//
// Por eso se guardan en la primera visita y se leen recién al crear la cuenta.
//
// ─── Primer toque, y no el último ──────────────────────────────────────────
//
// Si alguien entra por un anuncio, se va, vuelve por una búsqueda y recién ahí
// se registra, el alta se le atribuye al anuncio. Es la convención y es la que
// corresponde: el anuncio fue lo que hizo que esa persona supiera que
// existimos. Guardar el último toque haría que la marca propia se robara el
// crédito de la publicidad que la hizo conocida.
//
// ─── Treinta días ──────────────────────────────────────────────────────────
//
// Pasado ese plazo, un alta que todavía arrastre el utm de un anuncio viejo
// ensucia más de lo que informa. Es la ventana que usa Meta por defecto.
//
// ─── Dónde NO corre ────────────────────────────────────────────────────────
//
// En las páginas de reserva de los negocios. Esto se guarda en localStorage,
// que es exactamente lo que privacidad.html promete que ahí no pasa, así que
// la captura cuelga de la misma compuerta que el resto: ver lib/medicion.js.

const CLAVE = 'ht_utm';
const VENTANA_MS = 30 * 24 * 60 * 60 * 1000;

/** Los cinco que define el estándar. Tiene que coincidir con CAMPOS_UTM del api. */
export const CAMPOS_UTM = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'
];

// localStorage tira en vez de devolver vacío en algunos navegadores: modo
// privado de Safari viejo, cookies de terceros apagadas del todo, políticas
// corporativas. Nunca puede romper un registro, así que todo va envuelto.
function leerCrudo() {
  try {
    const texto = window.localStorage.getItem(CLAVE);
    return texto ? JSON.parse(texto) : null;
  } catch {
    return null;
  }
}

/**
 * Guarda los UTM de la URL actual, si los hay y si no había nada guardado.
 *
 * No pisa lo que ya estaba: ver "primer toque" arriba.
 */
export function capturarUtm(search = window.location?.search ?? '') {
  let campos;
  try {
    const parametros = new URLSearchParams(search);
    campos = {};
    for (const campo of CAMPOS_UTM) {
      const valor = parametros.get(campo);
      // El recorte en 200 es el mismo que el del esquema del api. Acá no es
      // por seguridad sino para no llenar el localStorage de alguien con una
      // URL armada a mano.
      if (valor) campos[campo] = valor.slice(0, 200);
    }
  } catch {
    return;
  }

  if (Object.keys(campos).length === 0) return;
  if (leerCrudo()) return;

  try {
    window.localStorage.setItem(CLAVE, JSON.stringify({
      campos,
      en: new Date().toISOString()
    }));
  } catch { /* sin localStorage se pierde la atribución, no el alta */ }
}

/**
 * Lo guardado, si no venció. Siempre un objeto: `{}` cuando no hay nada.
 *
 * Se esparce directo en el cuerpo del alta, así que `{}` significa "no mandes
 * ningún utm" sin que quien llama tenga que preguntar.
 */
export function utmGuardados() {
  const guardado = leerCrudo();
  if (!guardado?.campos) return {};

  const nacido = Date.parse(guardado.en ?? '');
  // Sin fecha legible no se puede saber si venció. Se descarta: es preferible
  // perder una atribución que atribuirle un alta de hoy a una campaña del año
  // pasado y decidir el presupuesto con eso.
  if (!Number.isFinite(nacido) || Date.now() - nacido > VENTANA_MS) {
    olvidarUtm();
    return {};
  }

  // Sólo los campos conocidos: lo que vuelve de acá se manda al api, y el
  // esquema de allá es .strict(). Una clave de más —puesta a mano en el
  // localStorage por cualquiera— haría fallar el alta con un 422.
  const limpios = {};
  for (const campo of CAMPOS_UTM) {
    const valor = guardado.campos[campo];
    if (typeof valor === 'string' && valor) limpios[campo] = valor.slice(0, 200);
  }
  return limpios;
}

/** Después de un alta: ya se usó, y dejarlo atribuiría también a la siguiente. */
export function olvidarUtm() {
  try {
    window.localStorage.removeItem(CLAVE);
  } catch { /* nada que olvidar */ }
}
